import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { api, fmtErr, API } from "../../lib/api";
import { Plus, PencilSimple, Trash, Star, Download } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";

const EMPTY = { name: "", categories: [], contact_person: "", contact_email: "", contact_phone: "", avg_lead_weeks: "", quality_rating: 3, last_order_date: "", active: true };

export default function ScmVendors() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [packs, setPacks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const orgIndustries = user?.org?.industries || [];

  const load = async () => {
    try {
      const [v, p] = await Promise.all([api.get("/vendors"), api.get("/industry-packs")]);
      setVendors(v.data); setPacks(p.data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    packs.filter((p) => orgIndustries.includes(p.key)).forEach((p) =>
      (p.bom_template || []).forEach((n) => set.add(n.name)));
    return [...set].sort();
  }, [packs, orgIndustries]);

  const save = async () => {
    if (!draft.name.trim()) { toast.error("Vendor name required"); return; }
    const payload = { ...draft, avg_lead_weeks: draft.avg_lead_weeks === "" ? null : Number(draft.avg_lead_weeks), quality_rating: Number(draft.quality_rating) };
    try {
      if (draft.id) await api.patch(`/vendors/${draft.id}`, payload);
      else await api.post("/vendors", payload);
      toast.success(draft.id ? "Vendor updated" : "Vendor added");
      setShowForm(false); setDraft(EMPTY); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (v) => {
    try { await api.delete(`/vendors/${v.id}`); toast.success("Vendor deleted"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const Stars = ({ n }) => (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={12} weight={i <= n ? "fill" : "regular"} className={i <= n ? "text-[var(--c-amber)]" : "text-[var(--c-border)]"} />
      ))}
    </span>
  );

  return (
    <div className="space-y-4" data-testid="scm-vendors">
      <div className="flex items-center justify-between">
        <div className="text-xs text-[var(--c-muted)]">{vendors.length} approved vendors · categories follow your industry packs' BOM groups</div>
        <div className="flex items-center gap-2">
          <Button data-testid="export-vendors-btn" variant="outline" onClick={() => window.open(`${API}/vendors/export.xlsx`, "_blank")}
            className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">
            <Download size={14} className="mr-1" /> Export Excel
          </Button>
          <Button data-testid="add-vendor-btn" onClick={() => { setDraft(EMPTY); setShowForm(true); }}
            className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
            <Plus size={14} className="mr-1" /> Add Vendor
          </Button>
        </div>
      </div>

      <div className="border border-[var(--c-border)] rounded-sm bg-[var(--c-surface)] overflow-x-auto">
        <table className="w-full text-sm" data-testid="vendors-table">
          <thead>
            <tr className="border-b border-[var(--c-border)]">
              {["Vendor", "Categories", "Contact", "Avg Lead", "Quality", "Last Order", "Active", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {vendors.map((v) => (
              <tr key={v.id} data-testid={`vendor-row-${v.id.slice(0, 8)}`} className="border-b border-[var(--c-border)]/60 last:border-0 hover:bg-[var(--c-border)]/25 transition-colors duration-150">
                <td className="px-4 py-3 text-xs font-medium text-[var(--c-text)]">{v.name}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(v.categories || []).map((c) => (
                      <span key={c} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[var(--c-accent)]/10 border border-[var(--c-accent)]/40 text-[var(--c-text)]">{c}</span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-[var(--c-muted)]">{v.contact_person || "—"}<div className="text-[10px]">{v.contact_email}</div></td>
                <td className="px-4 py-3 font-mono text-xs text-[var(--c-text)]">{v.avg_lead_weeks != null ? `${v.avg_lead_weeks}w` : "—"}</td>
                <td className="px-4 py-3"><Stars n={v.quality_rating || 0} /></td>
                <td className="px-4 py-3 font-mono text-xs text-[var(--c-muted)]">{v.last_order_date || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-mono uppercase ${v.active ? "text-[var(--c-green)]" : "text-[var(--c-muted)]"}`}>{v.active ? "Active" : "Off"}</span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`vendor-edit-${v.id.slice(0, 8)}`} onClick={() => { setDraft({ ...EMPTY, ...v }); setShowForm(true); }}
                    className="p-1 text-[var(--c-muted)] hover:text-[var(--c-accent)]"><PencilSimple size={13} /></button>
                  <button data-testid={`vendor-delete-${v.id.slice(0, 8)}`} onClick={() => remove(v)}
                    className="p-1 text-[var(--c-muted)] hover:text-[var(--c-red)]"><Trash size={13} /></button>
                </td>
              </tr>
            ))}
            {vendors.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-[var(--c-muted)]" data-testid="vendors-empty">No vendors yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] max-w-xl" data-testid="vendor-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--c-text)]">{draft.id ? "Edit Vendor" : "Add Vendor"}</DialogTitle>
            <DialogDescription className="sr-only">Vendor master data</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Vendor name *</Label>
              <Input data-testid="vf-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Categories (from industry pack BOM groups)</Label>
              <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto" data-testid="vf-categories">
                {categoryOptions.map((c) => {
                  const on = draft.categories.includes(c);
                  return (
                    <button key={c} type="button" data-testid={`vf-cat-${c.replace(/[^a-z0-9]+/gi, "-")}`}
                      onClick={() => setDraft((d) => ({ ...d, categories: on ? d.categories.filter((x) => x !== c) : [...d.categories, c] }))}
                      className={`px-2 py-0.5 rounded-sm text-[10px] border transition-colors duration-150 ${
                        on ? "bg-[var(--c-accent)]/15 border-[var(--c-accent)] text-[var(--c-text)]" : "border-[var(--c-border)] text-[var(--c-muted)] hover:border-[var(--c-muted)]"}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Contact person</Label>
                <Input data-testid="vf-contact" value={draft.contact_person} onChange={(e) => setDraft({ ...draft, contact_person: e.target.value })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Email</Label>
                <Input data-testid="vf-email" value={draft.contact_email} onChange={(e) => setDraft({ ...draft, contact_email: e.target.value })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Phone</Label>
                <Input data-testid="vf-phone" value={draft.contact_phone} onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs font-mono" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Avg lead (weeks)</Label>
                <Input data-testid="vf-lead" type="number" value={draft.avg_lead_weeks ?? ""} onChange={(e) => setDraft({ ...draft, avg_lead_weeks: e.target.value })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Quality rating (1-5)</Label>
                <div className="flex gap-1 pt-1.5" data-testid="vf-rating">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <button key={i} type="button" onClick={() => setDraft({ ...draft, quality_rating: i })}>
                      <Star size={18} weight={i <= draft.quality_rating ? "fill" : "regular"}
                        className={i <= draft.quality_rating ? "text-[var(--c-amber)]" : "text-[var(--c-border)] hover:text-[var(--c-muted)]"} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Last order date</Label>
                <Input data-testid="vf-last-order" type="date" value={draft.last_order_date} onChange={(e) => setDraft({ ...draft, last_order_date: e.target.value })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs font-mono" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch data-testid="vf-active" checked={draft.active} onCheckedChange={(v) => setDraft({ ...draft, active: v })} />
              <Label className="text-[var(--c-muted)] text-xs">Active (approved for RFQs)</Label>
            </div>
            <Button data-testid="vf-save" onClick={save} className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">
              {draft.id ? "Update Vendor" : "Add Vendor"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
